/**
 * TreatX Organization Roles Context — Phase 2C
 * ===============================================
 * REMOVED: sessionStorage for users and invitations
 * REMOVED: mock users and demo invitations
 *
 * Backend APIs used:
 *   GET/POST/PUT/DELETE /api/org/members      — org member CRUD
 *   GET/POST/PUT        /api/org/invitations   — invitation lifecycle
 */
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuthGate } from '../hooks/useAuthGate';
const _ORG_ROLES = ['clinic_admin', 'provider_admin', 'admin', 'treatx_admin', 'super_admin', 'platform_admin'];
import axios from 'axios';

const OrganizationRolesContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useOrganizationRoles = () => {
  const context = useContext(OrganizationRolesContext);
  if (!context) {
    throw new Error('useOrganizationRoles must be used within OrganizationRolesProvider');
  }
  return context;
};

// OF-001D #3 — Legacy/dormant organization roles map.  The CANONICAL
// labels here come from `roleLabel()` to stay drift-locked with the
// rest of the platform.  This map is being phased out in favour of
// `INVITE_ALLOWED_PROVIDER_ROLES` + `roleLabel()`; any new consumer
// must use the canonical resolver, NOT this dictionary.
import { roleLabel } from '../utils/canonicalRoleLabels';

export const ORGANIZATION_ROLES = {
  admin:       { id: 'provider_admin',            name: roleLabel('provider_admin'),            description: 'Full access',                              permissions: ['all'],                                                                  color: 'purple' },
  sales_rep:   { id: 'provider_sales_rep',        name: roleLabel('provider_sales_rep'),        description: 'Access to leads and CRM',                  permissions: ['view_leads', 'manage_leads', 'send_offers', 'messaging'],               color: 'blue' },
  post_sales:  { id: 'provider_after_sales_rep',  name: roleLabel('provider_after_sales_rep'),  description: 'Post-sale coordination',                   permissions: ['view_confirmed_patients', 'manage_travel', 'manage_timeline', 'messaging', 'lead_assign'], color: 'cyan' },
  doctor:      { id: 'provider_doctor',           name: roleLabel('provider_doctor'),           description: 'View patients and medical records',        permissions: ['view_assigned_patients', 'view_medical_records', 'add_notes'],          color: 'teal' },
  coordinator: { id: 'provider_coordinator',      name: roleLabel('provider_coordinator'),      description: 'Team oversight and lead assignment',       permissions: ['view_patients', 'messaging', 'scheduling'],                             color: 'indigo' },
};

export const INVITATION_STATUS = {
  INVITED: 'invited', PENDING: 'pending', ACCEPTED: 'accepted',
  EXPIRED: 'expired', CANCELLED: 'cancelled', REVOKED: 'revoked',
};

export const USER_STATUS = { ACTIVE: 'active', INACTIVE: 'inactive', SUSPENDED: 'suspended' };

const normalizeMember = (m) => ({
  id: m.member_id,
  identity: { email: m.email, phone: m.phone || '' },
  name: m.name,
  avatar: m.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name)}&background=random`,
  organizations: (m.organizations || []).map(o => ({
    organizationId: o.organization_id,
    organizationName: o.organization_name,
    role: o.role,
    status: o.status,
    joinedAt: o.joined_at,
  })),
  createdAt: m.created_at,
});

const normalizeInvitation = (i) => ({
  id: i.invitation_id,
  organizationId: i.organization_id,
  organizationName: i.organization_name,
  inviteeEmail: i.invitee_email,
  inviteePhone: i.invitee_phone || '',
  inviteeName: i.invitee_name || '',
  role: i.role,
  isExistingUser: false,
  existingUserId: null,
  status: i.status,
  invitedBy: i.invited_by,
  inviteToken: i.invite_token,
  createdAt: i.created_at,
  expiresAt: i.expires_at,
  emailSentAt: i.email_sent_at,
});

export const OrganizationRolesProvider = ({ children }) => {
  const [users, setUsers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [accessHistory, setAccessHistory] = useState([]);

  const fetchMembers = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/org/members`);
      setUsers((data.members || []).map(normalizeMember));
    } catch (err) { console.error('[OrgRoles] fetchMembers error:', err); }
  }, []);

  const fetchInvitations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/org/invitations`);
      setInvitations((data.invitations || []).map(normalizeInvitation));
    } catch (err) { console.error('[OrgRoles] fetchInvitations error:', err); }
  }, []);

  // TREATX-BATCH-BOOT-01 — org members/invitations are clinic-admin
  // or TreatX-admin scope only.
  const { authorized: _org_authorized } = useAuthGate({ roles: _ORG_ROLES });
  useEffect(() => {
    if (!_org_authorized) return;
    fetchMembers();
    fetchInvitations();
  }, [_org_authorized, fetchMembers, fetchInvitations]);

  const findUserByIdentity = useCallback((identifier) => {
    return users.find(u =>
      u.identity.email?.toLowerCase() === identifier?.toLowerCase() ||
      u.identity.phone === identifier
    );
  }, [users]);

  const getUserById = useCallback((userId) => users.find(u => u.id === userId), [users]);

  const getOrganizationUsers = useCallback((organizationId, includeInactive = false) => {
    return users.filter(u =>
      u.organizations.some(org =>
        org.organizationId === organizationId &&
        (includeInactive || org.status === USER_STATUS.ACTIVE)
      )
    ).map(u => {
      const orgMembership = u.organizations.find(org => org.organizationId === organizationId);
      return { ...u, role: orgMembership?.role, roleInfo: ORGANIZATION_ROLES[orgMembership?.role], membershipStatus: orgMembership?.status, joinedAt: orgMembership?.joinedAt };
    });
  }, [users]);

  const sendInvitation = useCallback(async (organizationId, organizationName, inviteeData, role, invitedBy) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/org/invitations`, {
        organization_id: organizationId,
        organization_name: organizationName,
        invitee_email: inviteeData.email || '',
        invitee_phone: inviteeData.phone || '',
        invitee_name: inviteeData.name || '',
        role,
        invited_by: invitedBy,
      });
      if (data.success) {
        await fetchInvitations();
        return normalizeInvitation(data.invitation);
      }
    } catch (err) { console.error('[OrgRoles] sendInvitation error:', err); }
    return null;
  }, [fetchInvitations]);

  const getInvitationByToken = useCallback((token) => invitations.find(inv => inv.inviteToken === token), [invitations]);

  const markInvitationAsPending = useCallback((invitationId) => {
    setInvitations(prev => prev.map(inv =>
      inv.id === invitationId && inv.status === INVITATION_STATUS.INVITED
        ? { ...inv, status: INVITATION_STATUS.PENDING, viewedAt: new Date().toISOString() }
        : inv
    ));
  }, []);

  const acceptInvitation = useCallback(async (invitationId, userData = {}) => {
    try {
      const { data } = await axios.put(`${API_URL}/api/org/invitations/${invitationId}/accept`, {
        name: userData.name || '',
        avatar: userData.avatar || '',
      });
      if (data.success) {
        await fetchInvitations();
        await fetchMembers();
        return normalizeInvitation(data.invitation);
      }
    } catch (err) { console.error('[OrgRoles] acceptInvitation error:', err); }
    return null;
  }, [fetchInvitations, fetchMembers]);

  const cancelInvitation = useCallback(async (invitationId, cancelledBy = 'Admin') => {
    try {
      await axios.put(`${API_URL}/api/org/invitations/${invitationId}/cancel?cancelled_by=${cancelledBy}`);
      await fetchInvitations();
    } catch (err) { console.error('[OrgRoles] cancelInvitation error:', err); }
  }, [fetchInvitations]);

  const resendInvitation = useCallback(async (invitationId, resentBy = 'Admin') => {
    try {
      const { data } = await axios.put(`${API_URL}/api/org/invitations/${invitationId}/resend?resent_by=${resentBy}`);
      if (data.success) {
        await fetchInvitations();
        return normalizeInvitation(data.invitation);
      }
    } catch (err) { console.error('[OrgRoles] resendInvitation error:', err); }
    return null;
  }, [fetchInvitations]);

  const revokeInvitation = useCallback((invitationId) => cancelInvitation(invitationId, 'Admin'), [cancelInvitation]);

  const declineInvitation = useCallback(async (invitationId) => {
    await cancelInvitation(invitationId, 'Invitee');
  }, [cancelInvitation]);

  const changeUserRole = useCallback(async (userId, organizationId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/org/members/${userId}/role?organization_id=${organizationId}`, { role: newRole });
      await fetchMembers();
    } catch (err) { console.error('[OrgRoles] changeUserRole error:', err); }
  }, [fetchMembers]);

  const deactivateUserFromOrg = useCallback(async (userId, organizationId) => {
    try {
      await axios.put(`${API_URL}/api/org/members/${userId}/deactivate?organization_id=${organizationId}`);
      await fetchMembers();
    } catch (err) { console.error('[OrgRoles] deactivateUserFromOrg error:', err); }
  }, [fetchMembers]);

  const reactivateUserInOrg = useCallback(async (userId, organizationId) => {
    try {
      await axios.put(`${API_URL}/api/org/members/${userId}/reactivate?organization_id=${organizationId}`);
      await fetchMembers();
    } catch (err) { console.error('[OrgRoles] reactivateUserInOrg error:', err); }
  }, [fetchMembers]);

  const removeUserFromOrg = useCallback(async (userId, organizationId) => {
    try {
      await axios.delete(`${API_URL}/api/org/members/${userId}?organization_id=${organizationId}`);
      await fetchMembers();
    } catch (err) { console.error('[OrgRoles] removeUserFromOrg error:', err); }
  }, [fetchMembers]);

  const getOrganizationInvitations = useCallback((organizationId, statusFilter = null) => {
    return invitations.filter(inv => {
      if (inv.organizationId !== organizationId) return false;
      if (statusFilter && inv.status !== statusFilter) return false;
      return true;
    });
  }, [invitations]);

  const getPendingInvitations = useCallback((organizationId) => {
    return invitations.filter(inv =>
      inv.organizationId === organizationId &&
      (inv.status === INVITATION_STATUS.INVITED || inv.status === INVITATION_STATUS.PENDING)
    );
  }, [invitations]);

  const getAccessHistory = useCallback((organizationId) => accessHistory.filter(log => log.organizationId === organizationId), [accessHistory]);

  const hasPermission = useCallback((userId, organizationId, permission) => {
    const user = users.find(u => u.id === userId);
    if (!user) return false;
    const org = user.organizations.find(o => o.organizationId === organizationId && o.status === USER_STATUS.ACTIVE);
    if (!org) return false;
    const role = ORGANIZATION_ROLES[org.role];
    return role?.permissions.includes('all') || role?.permissions.includes(permission);
  }, [users]);

  const getUserCountByRole = useCallback((organizationId) => {
    const orgUsers = getOrganizationUsers(organizationId);
    return Object.keys(ORGANIZATION_ROLES).reduce((acc, roleId) => {
      acc[roleId] = orgUsers.filter(u => u.role === roleId).length;
      return acc;
    }, {});
  }, [getOrganizationUsers]);

  const getActiveUserCount = useCallback((organizationId) => getOrganizationUsers(organizationId).length, [getOrganizationUsers]);

  const value = {
    users, invitations, accessHistory,
    ORGANIZATION_ROLES, INVITATION_STATUS, USER_STATUS,
    findUserByIdentity, getUserById, getOrganizationUsers, getUserCountByRole, getActiveUserCount,
    sendInvitation, getInvitationByToken, markInvitationAsPending, acceptInvitation, declineInvitation,
    cancelInvitation, resendInvitation, revokeInvitation,
    getPendingInvitations, getOrganizationInvitations,
    changeUserRole, deactivateUserFromOrg, reactivateUserInOrg, removeUserFromOrg,
    hasPermission, getAccessHistory,
  };

  return (
    <OrganizationRolesContext.Provider value={value}>
      {children}
    </OrganizationRolesContext.Provider>
  );
};

export default OrganizationRolesContext;
